import { isNil } from "@sunrise/utils";
import { type GuideChannel } from "@sunrise/yallo-guide";
import { VirtualItem } from "@tanstack/react-virtual";
import clsx from "clsx";
import { ReactElement } from "react";

import { ChannelLogo } from "@/components/channel-logo/channel-logo";
import { ChannelLockIndicator } from "@/features/channel/channel-lock-indicator";
import * as styles from "@/features/guide/guide-channels.css";

type GuideChannelItemProps = {
  size: number;
  items: VirtualItem[];
  channels: GuideChannel[];
  channelHeightInPx: number;
} & CommonProps;

export const GuideChannels = ({
  size,
  items,
  channels,
  channelHeightInPx,
  className,
  "data-testid": dataTestId = "guide-channel-bar",
}: GuideChannelItemProps): ReactElement => {
  /**
   * A sticky positioned bar at the left side of the grid.
   * It contains the channel name for every channel and it will always be visible.
   */
  return (
    <div
      className={clsx([styles.channelBar, className])}
      data-testid={dataTestId}
      style={{
        height: size,
      }}
    >
      {items.map((virtualItem) => {
        const channel = channels[virtualItem.index];

        if (isNil(channel)) {
          return null;
        }

        return (
          <div
            key={virtualItem.key}
            data-testid={`${dataTestId}-${channel.id}`}
            className={styles.channelBarChannelTitle}
            style={{
              height: channelHeightInPx,
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            <div className={styles.container}>
              <ChannelLockIndicator
                channelId={channel.id}
                className={styles.lockIndicator}
                data-testid={`${dataTestId}-${channel.id}.channel-lock-indicator`}
                borderRadius={0}
              />
              <div className={styles.channelNo}>{channel.channelNumber}</div>
              {channel.logo?.url ? (
                <ChannelLogo url={channel.logo.url} className={styles.logo} />
              ) : (
                <span data-testid={`${dataTestId}-${channel.id}.channel-name`}>
                  {channel.name}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
